import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { Share2, Stethoscope, AlertTriangle, X, Globe, GlassWater } from 'lucide-react';
import App from './App';
import ResultPage from './ResultPage';

const DoctorSelection: React.FC = () => {
  const navigate = useNavigate();
  const [showMartyModal, setShowMartyModal] = useState(false);
  
  const handleSelect = (dr: 'matty' | 'marty') => {
    if (dr === 'marty') {
      setShowMartyModal(true);
    } else {
      navigate(`/survey/${dr}`);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow max-w-lg w-full">
        <h2 className="text-2xl font-bold text-center mb-6 text-gray-900 dark:text-white">Choose Your Doctor</h2>
        <div className="grid grid-cols-2 gap-6">
          <button 
            className="flex flex-col items-center p-6 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            onClick={() => handleSelect('matty')}
          >
            <Stethoscope size={32} />
            <span className="mt-2 font-semibold">Dr. Matty</span>
            <small className="text-sm">A charming liar with impeccable bedside manner</small>
          </button>
          
          <button 
            className="flex flex-col items-center p-6 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition relative"
            onClick={() => handleSelect('marty')}
          >
            <div className="absolute -top-2 -right-2 bg-amber-500 text-white px-2 py-1 rounded text-xs font-bold animate-pulse">
              UNAVAILABLE
            </div>
            <Stethoscope size={32} />
            <span className="mt-2 font-semibold">Dr. Marty</span>
            <small className="text-sm">An honest broker but totally unsympathetic to your pain</small>
          </button>
        </div>
      </div>
      
      {/* Modal for Dr. Marty being unavailable */}
      {showMartyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full shadow-xl relative">
            <button 
              onClick={() => setShowMartyModal(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X size={24} />
            </button>
            
            <div className="text-center mb-4">
              <div className="mx-auto w-20 h-20 bg-amber-100 dark:bg-amber-900 rounded-full flex items-center justify-center mb-4">
                <GlassWater size={32} className="text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Dr. Marty is Unavailable</h3>
            </div>
            
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p>
                We regret to inform you that Dr. Marty is currently attending the annual <span className="font-semibold italic">Bed Whisky Conference</span> in Scotland.
              </p>
              <p>
                According to his office calendar, he's scheduled for a "comprehensive tasting" session that spans the entire week.
              </p>
              <div className="bg-amber-50 dark:bg-gray-700 p-4 rounded-lg mt-4">
                <p className="italic text-sm">
                  "Tell those weaklings to suck it up. I'll be back when I'm good and ready, and not a minute sooner." - Dr. Marty
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => {
                  setShowMartyModal(false);
                  navigate('/survey/matty');
                }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg"
              >
                See Dr. Matty Instead
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MainAppRoutes: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<DoctorSelection />} />
        <Route path="/survey/:doctor" element={<App />} />
        <Route path="/results/:resultId" element={<ResultPage />} />
      </Routes>
    </Router>
  );
};

export default MainAppRoutes;